import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Input } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styles from './CommandPalette.module.css';

export interface PaletteEntry {
  id: string;
  group: string;
  label: string;
  hint?: string;
  keywords?: string;
  icon?: React.ReactNode;
  active?: boolean;
  run: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entries: PaletteEntry[];
}

// Highlight the matched substring in a label.
const highlight = (text: string, q: string): React.ReactNode => {
  const s = q.trim();
  if (!s) return text;
  const i = text.toLowerCase().indexOf(s.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className={styles.mark}>{text.slice(i, i + s.length)}</mark>
      {text.slice(i + s.length)}
    </>
  );
};

const CommandPalette: React.FC<Props> = ({ open, onClose, entries }) => {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<InputRef>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter(
      (e) =>
        e.label.toLowerCase().includes(s) ||
        (e.hint || '').toLowerCase().includes(s) ||
        (e.keywords || '').toLowerCase().includes(s) ||
        e.group.toLowerCase().includes(s)
    );
  }, [q, entries]);

  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => setCursor(0), [q]);

  // Keep the active row visible as the cursor moves.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const select = (e: PaletteEntry) => {
    e.run();
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[cursor]) select(filtered[cursor]);
    }
  };

  // Group filtered entries (preserve first-seen order); keep flat index for the cursor.
  const groups: { name: string; entries: { e: PaletteEntry; idx: number }[] }[] = [];
  filtered.forEach((e, idx) => {
    let g = groups.find((x) => x.name === e.group);
    if (!g) {
      g = { name: e.group, entries: [] };
      groups.push(g);
    }
    g.entries.push({ e, idx });
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={540}
      styles={{ body: { padding: 0 } }}
      className={styles.modal}
      destroyOnClose
    >
      <Input
        ref={inputRef}
        size="large"
        bordered={false}
        prefix={<SearchOutlined className={styles.searchIcon} />}
        placeholder="Пошук сторінки або команди…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
        className={styles.input}
        aria-activedescendant={filtered.length ? `cp-item-${cursor}` : undefined}
      />
      <div className={styles.list} ref={listRef} role="listbox">
        {filtered.length === 0 && <div className={styles.empty}>Нічого не знайдено</div>}
        {groups.map((g) => (
          <div key={g.name} className={styles.group}>
            <div className={styles.groupHead}>{g.name}</div>
            {g.entries.map(({ e, idx }) => (
              <button
                key={e.id}
                type="button"
                id={`cp-item-${idx}`}
                role="option"
                aria-selected={idx === cursor}
                data-idx={idx}
                className={`${styles.item} ${idx === cursor ? styles.itemActive : ''}`}
                onMouseMove={() => {
                  // Not onMouseEnter: a parked cursor must not steal the active
                  // row from arrow-key navigation.
                  if (cursor !== idx) setCursor(idx);
                }}
                onClick={() => select(e)}
              >
                {e.icon && <span className={styles.itemIcon}>{e.icon}</span>}
                <span className={styles.itemLabel}>{highlight(e.label, q)}</span>
                {e.active ? (
                  <span className={styles.current}>тут</span>
                ) : (
                  e.hint && <span className={styles.itemSection}>{e.hint}</span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <span>
          <kbd className={styles.fkbd}>↑</kbd>
          <kbd className={styles.fkbd}>↓</kbd> навігація
        </span>
        <span>
          <kbd className={styles.fkbd}>↵</kbd> відкрити
        </span>
        <span>
          <kbd className={styles.fkbd}>esc</kbd> закрити
        </span>
      </div>
    </Modal>
  );
};

export default CommandPalette;
